import React from "react"
import styled, { css } from "styled-components"

import { useLazyGetWarehouseListQuery } from "store/api/warehousesApi"
import { orderTypeOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"
import { ReactComponent as IconDevice } from "assets/svg/device.svg"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"
import { ReactComponent as WireAdapterIcon } from "assets/svg/wire_adapter.svg"

import { OrderStatusBadge } from "components/common/OrderStatusBadge"
import { InputNumber } from "components/form/InputNumber"
import { SelectDynamic } from "components/form/SelectDynamic"
import { SelectStatic } from "components/form/SelectStatic"

import { OrderItem } from "./index"

const OrderItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background-color: #f8fafc;
  border-radius: 8px;
  margin-bottom: 12px;

  @media (max-width: 770px) {
    flex-wrap: wrap;
  }
`

const OrderItemIcon = styled.div`
  svg {
    width: 64px;
    height: 64px;
  }
`

const OrderItemField = styled.div<{ isShort?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;

  ${({ isShort }) =>
    isShort &&
    css`
      flex: 0.8;
    `}

  @media (max-width: 770px) {
    min-width: 150px;
  }
`

const OrderItemValue = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
`

const OrderItemQty = styled.span`
  font-size: 14px;
  color: #64748b;
`

const OrderItemDelete = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    stroke: #0f172a;
  }

  &:hover {
    background-color: #f8fafc;

    svg {
      stroke: #ef4444;
    }
  }
`

interface OrderItemRowProps {
  item: OrderItem
  isEdit?: boolean
  index: number
  orderItems: OrderItem[]
  setOrderItems: React.Dispatch<React.SetStateAction<OrderItem[]>>
  isExisting?: boolean
}

export const OrderItemRow = ({
  item,
  isEdit,
  index,
  orderItems,
  setOrderItems,
  isExisting = false,
}: OrderItemRowProps) => {
  const Icon = item.productType === "thermostat" ? IconDevice : WireAdapterIcon
  const productLabel = item.productType === "thermostat" ? "Nuve Thermostats" : "C Wire Adapters (CWA)"

  const handleQuantityChange = (value: number) => {
    setOrderItems(orderItems.map((item, i) => (i === index ? { ...item, quantity: value } : item)))
  }

  const handleOrderTypeChange = (values: UITypes.Option[]) => {
    if (!values.length) return
    setOrderItems(orderItems.map((item, i) => (i === index ? { ...item, orderType: values } : item)))
  }

  const handleWarehouseChange = (values: UITypes.Option[]) => {
    setOrderItems(orderItems.map((item, i) => (i === index ? { ...item, warehouse: values } : item)))
  }

  const handleDelete = () => {
    setOrderItems(orderItems.filter((_, i) => i !== index))
  }

  return (
    <OrderItemWrapper>
      <OrderItemIcon>
        <Icon />
      </OrderItemIcon>
      <OrderItemField>
        <OrderItemValue>{productLabel}</OrderItemValue>
        <OrderItemQty>Qty: {item.quantity}</OrderItemQty>
      </OrderItemField>
      <OrderItemField>
        <InputNumber minValue={1} onInputChange={handleQuantityChange} value={item.quantity} />
      </OrderItemField>
      <OrderItemField>
        <SelectStatic
          placeholder="Order type"
          values={item.orderType}
          options={orderTypeOptions}
          onChange={handleOrderTypeChange}
          isSearchable={false}
          backspaceDelete={false}
        />
      </OrderItemField>
      <OrderItemField>
        <SelectDynamic
          values={item.warehouse}
          valueName="id"
          labelName="name"
          name={`warehouses-${item.id}`}
          useLazyQuery={useLazyGetWarehouseListQuery}
          placeholder="Warehouse"
          onChange={handleWarehouseChange}
          className=""
        />
      </OrderItemField>
      {isExisting && item.status && (
        <OrderItemField>
          <div>
            <OrderStatusBadge status={item.status} />
          </div>
        </OrderItemField>
      )}
      {!isExisting && !isEdit && (
        <OrderItemDelete onClick={handleDelete}>
          <IconDelete />
        </OrderItemDelete>
      )}
      {!isExisting && isEdit && (
        <OrderItemField>
          <OrderItemDelete onClick={handleDelete}>
            <IconDelete />
          </OrderItemDelete>
        </OrderItemField>
      )}
    </OrderItemWrapper>
  )
}
